import { CardVariant } from "../../../models/CardVariant"
import Select from "react-select"
import { useEffect, useRef, useState } from "react";
import { ItemDisplayToolbarProps } from "../ItemDisplayToolbar";
import { CardImage } from "../../CardVariantImage/CardImage";
import { CrystalWithCost } from "../../Crystal/CrystalWithCost";
import { Button } from "../../Control/Button/Button";
import { FunnelIcon } from "@heroicons/react/24/outline";

export type CardDisplayQuickResultsProps<TItem extends CardVariant> = {
    isOpen: boolean,
    items: TItem[],
    searchText: string,
    onTypeStop: (searchText: string) => void,
    onOptionClick: (item: TItem) => void,
    onEnterPress: (searchText: string) => void,
    onTextChange: (searchText: string) => void
} & ItemDisplayToolbarProps;

export default function CardDisplayQuickResults<TItem extends CardVariant>(props: CardDisplayQuickResultsProps<TItem>) {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [selection, setSelection] = useState(null);

    useEffect(() => {
        setSearchText(props.searchText);
    }, [props.searchText]);

    return (
        <div className="space-y-4 max-w-6xl w-full text-contrast-primary">
            <div className={`grid grid-cols-1 md:grid-cols-[100px_1fr] gap-2`}>
                <Button onClick={() => props.onFiltersClick()} color="component" className="flex items-center justify-center gap-1 h-[38px]" title="Filters">
                    <FunnelIcon className="w-4 h-4" />
                    <div>{props.numberOfFiltersApplied || ""}</div>
                </Button>

                <form>
                    <Select
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.stopPropagation();
                                event.preventDefault();

                                setIsLoading(false);
                                setIsOpen(false);
                                if (timeoutRef.current) {
                                    clearTimeout(timeoutRef.current);
                                }

                                props.onEnterPress(searchText);
                                const inputElement = event?.target as HTMLInputElement;
                                inputElement?.blur();
                            }
                        }}
                        classNames={{
                            control: (internalProps) => `!bg-secondary !border !border-color-border !text-contrast-primary !h-[34px] !rounded-md !shadow-sm !cursor-text`,
                            placeholder: () => `!text-contrast-tertiary`,
                            input: () => `!text-contrast-primary !border-none !outline-none !caret-contrast-primary select-none`,
                            loadingIndicator: () => `!text-contrast-primary`,
                            menuList: () => `!text-contrast-primary !z-50`,
                            menuPortal: () => "!z-50",
                            menu: () => `!z-50 min-w-[300px] !bg-secondary !border !border-color-border/75`
                        }}
                        styles={{
                            input: (base) => ({
                                ...base,
                                'input:focus': {
                                    boxShadow: 'none'
                                }
                            })
                        }}
                        value={selection}
                        components={{
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null,
                            Option: (optionProps) => {
                                if (!optionProps?.data) {
                                    return <></>;
                                }

                                return (
                                    <div className="px-1 py-2 select-none hover:bg-primary flex items-center content-center text-contrast-primary" onClick={() => {
                                        props.onOptionClick(optionProps.data!);
                                    }}>
                                        <CrystalWithCost
                                            cost={optionProps.data!.card.cost ?? 0}
                                            elements={optionProps.data!.card.elements ?? []}
                                            classNames={{
                                                crystal: "h-10"
                                            }} />

                                        <CardImage variant={optionProps.data!} className="h-10 mr-6 ml-1" />
                                        <div className="w-[75px] min-w-[75px] font-semibold text-sm">{optionProps.data.serial}</div>
                                        <div>{optionProps.data.card.name}</div>
                                    </div>
                                )
                            }
                        }}
                        filterOption={() => true}
                        inputValue={searchText ?? ""}
                        onChange={undefined}
                        controlShouldRenderValue={false}
                        onInputChange={(newText, inputAction) => {
                            if (timeoutRef.current) {
                                clearTimeout(timeoutRef.current);
                            }

                            if (inputAction.action === "input-blur" || inputAction.action === "menu-close") {
                                setIsLoading(false);
                                setIsOpen(false);
                                return;
                            }

                            setSearchText(newText);
                            props.onTextChange(newText);
                            setIsLoading(true);

                            if (!newText) {
                                props.onTypeStop(newText);
                                setIsOpen(false);
                                setIsLoading(false);
                                return;
                            } else if (newText && !isOpen) {
                                setIsOpen(true);
                            }

                            timeoutRef.current = setTimeout(() => {
                                props.onTypeStop(newText);
                                setIsLoading(false);
                            }, 500);
                        }}
                        placeholder="Search Cards"
                        options={props.items}
                        isClearable={false}
                        menuIsOpen={isOpen}
                        blurInputOnSelect={true}
                        menuPosition="fixed"
                        isLoading={isLoading} />
                </form>
            </div>
        </div>
    )
}